import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { useRealtimeEvent } from '../contexts/RealtimeContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Package, Plus, Edit, Trash2, Eye, Clock, CheckCircle, XCircle, 
  ShoppingCart, Send, RefreshCw, AlertCircle, User, Calendar, Search, X,
  Download, FileSpreadsheet, TrendingUp, TrendingDown, DollarSign,
  CheckSquare, Ban, History, FileText, Filter
} from 'lucide-react';
import { DataTable } from '../components/ui/data-table';
import * as XLSX from 'xlsx';
import { ArticleQuantityDialog } from '../components/ui/article-quantity-dialog';

const MaterialRequestsPageEnhanced = () => {
  const { selectedPlant, selectedLocation, locations } = useData();
  const { user, hasRole } = useAuth();
  const [requests, setRequests] = useState([]);
  const [articles, setArticles] = useState([]);
  const [stockData, setStockData] = useState([]); // Pentru stoc disponibil
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showBulkApproveDialog, setShowBulkApproveDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [articleSearchTerm, setArticleSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [importanceFilter, setImportanceFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  const emptyForm = {
    location_id: '',
    importance: 'medium',
    notes: '',
    items: []
  };
  
  const [formData, setFormData] = useState(emptyForm);
  const [pendingArticleForRequest, setPendingArticleForRequest] = useState(null);

  const fetchDataQuiet = useCallback(async () => {
    try {
      const params = {};
      if (selectedPlant && selectedPlant !== 'all') params.plant_id = selectedPlant;
      if (selectedLocation && selectedLocation !== 'all') params.location_id = selectedLocation;
      const res = await api.get('/material-requests', { params });
      setRequests(res.data);
    } catch {}
  }, [selectedPlant, selectedLocation]);

  // Real-time: refresh on material request changes
  useRealtimeEvent('material_request', fetchDataQuiet);
  useRealtimeEvent('stock_change', fetchDataQuiet);

  useEffect(() => {
    fetchData();
  }, [selectedPlant, selectedLocation]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedPlant && selectedPlant !== 'all') params.plant_id = selectedPlant;
      if (selectedLocation && selectedLocation !== 'all') params.location_id = selectedLocation;
      
      const [requestsRes, articlesRes, stockRes] = await Promise.all([
        api.get('/material-requests', { params }),
        api.get('/articles'),
        api.get('/stock')
      ]);
      
      setRequests(requestsRes.data);
      setArticles(articlesRes.data);
      setStockData(stockRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  // Get available stock for an article
  const getAvailableStock = (articleId) => {
    const totalStock = stockData
      .filter(s => s.article_id === articleId)
      .reduce((sum, s) => sum + (s.quantity || 0), 0);
    return totalStock;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      toast.error('Adaugati cel putin un articol');
      return;
    }
    
    if (!formData.location_id) {
      toast.error('Selectati o locatie');
      return;
    }
    
    try {
      const payload = {
        ...formData,
        requester_id: user.id,
        requester_name: user.full_name,
        items_count: formData.items.length,
        total_value: formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0)
      };
      
      await api.post('/material-requests', payload);
      toast.success('Cerere creata cu succes!');
      setShowCreateDialog(false);
      setFormData(emptyForm);
      fetchData();
    } catch (error) {
      console.error('Create error:', error);
      toast.error(error.response?.data?.detail || 'Eroare la creare');
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    try {
      await api.put(`/material-requests/${requestId}`, { 
        status: newStatus,
        [newStatus === 'approved' ? 'approved_by' : 'rejected_by']: user.id,
        [newStatus === 'approved' ? 'approved_at' : 'rejected_at']: new Date().toISOString()
      });
      toast.success(`Cerere ${newStatus === 'approved' ? 'aprobata' : 'respinsa'}!`);
      fetchData();
      setShowViewDialog(false);
    } catch (error) {
      toast.error('Eroare la actualizare');
    }
  };

  const handleBulkApprove = async () => {
    try {
      const results = await Promise.all(
        selectedRequests.map(req => 
          api.put(`/material-requests/${req.id}`, {
            status: 'approved',
            approved_by: user.id,
            approved_at: new Date().toISOString()
          })
        )
      );
      toast.success(`${selectedRequests.length} cereri aprobate!`);
      setSelectedRequests([]);
      setShowBulkApproveDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Eroare la aprobare lot');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriti sa stergeti aceasta cerere?')) return;
    try {
      await api.delete(`/material-requests/${id}`);
      toast.success('Cerere stearsa!');
      fetchData();
    } catch (error) {
      toast.error('Eroare la stergere');
    }
  };

  const addItemToRequest = (article) => {
    setPendingArticleForRequest(article);
  };

  const confirmAddItemToRequest = (article, qty) => {
    const existingItem = formData.items.find(i => i.article_id === article.id);
    if (existingItem) {
      setFormData({
        ...formData,
        items: formData.items.map(i => 
          i.article_id === article.id 
            ? { ...i, quantity: i.quantity + qty }
            : i
        )
      });
    } else {
      setFormData({
        ...formData,
        items: [...formData.items, {
          article_id: article.id,
          article_name: article.name,
          article_code: article.code,
          unit: article.unit || 'buc',
          quantity: qty,
          unit_price: article.price || 0
        }]
      });
    }
    setArticleSearchTerm('');
    setPendingArticleForRequest(null);
  };

  const removeItemFromRequest = (articleId) => {
    setFormData({
      ...formData,
      items: formData.items.filter(i => i.article_id !== articleId)
    });
  };

  const updateItemQuantity = (articleId, quantity) => {
    setFormData({
      ...formData,
      items: formData.items.map(i => 
        i.article_id === articleId 
          ? { ...i, quantity: Math.max(1, parseFloat(quantity) || 1) }
          : i
      )
    });
  };

  // Export to Excel
  const handleExportExcel = () => {
    try {
      const exportData = filteredRequests.map(req => ({
        'Nr. Cerere': req.id.substring(0, 8),
        'Data': formatDate(req.created_at),
        'Solicitant': req.requester_name,
        'Locatie': req.location_name,
        'Importanta': req.importance,
        'Status': req.status,
        'Nr. Articole': req.items_count,
        'Valoare Totala': req.total_value?.toFixed(2) || '0.00',
        'Aprobat de': req.approved_by_name || '-',
        'Data Aprobare': req.approved_at ? formatDate(req.approved_at) : '-',
        'Note': req.notes || '-'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Cereri Consumabile');
      
      // Auto width columns
      const maxWidth = exportData.reduce((w, r) => Math.max(w, ...Object.values(r).map(v => String(v).length)), 10);
      ws['!cols'] = Object.keys(exportData[0] || {}).map(() => ({ wch: Math.min(maxWidth, 30) }));
      
      XLSX.writeFile(wb, `cereri_consumabile_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Export Excel generat!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Eroare la export Excel');
    }
  };

  // Export detailed report
  const handleExportDetailedExcel = () => {
    try {
      const detailedData = [];
      
      filteredRequests.forEach(req => {
        req.items?.forEach((item, idx) => {
          detailedData.push({
            'ID Cerere': req.id.substring(0, 8),
            'Data Cerere': formatDate(req.created_at),
            'Solicitant': req.requester_name,
            'Locatie': req.location_name,
            'Status Cerere': req.status,
            'Importanta': req.importance,
            'Articol Cod': item.article_code,
            'Articol Nume': item.article_name,
            'Cantitate': item.quantity,
            'UM': item.unit,
            'Pret Unitar': item.unit_price?.toFixed(2) || '0.00',
            'Valoare': (item.quantity * item.unit_price).toFixed(2),
            'Aprobat de': req.approved_by_name || '-',
            'Data Aprobare': req.approved_at ? formatDate(req.approved_at) : '-'
          });
        });
      });

      const ws = XLSX.utils.json_to_sheet(detailedData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Detaliat');
      
      ws['!cols'] = Object.keys(detailedData[0] || {}).map(() => ({ wch: 15 }));
      
      XLSX.writeFile(wb, `cereri_detaliat_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Export detaliat generat!');
    } catch (error) {
      toast.error('Eroare la export');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      approved: 'bg-green-100 text-green-700 border-green-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
      completed: 'bg-blue-100 text-blue-700 border-blue-200',
      partially_delivered: 'bg-orange-100 text-orange-700 border-orange-200'
    };
    const labels = {
      pending: 'In asteptare',
      approved: 'Aprobat',
      rejected: 'Respins',
      completed: 'Livrat',
      partially_delivered: 'Partial livrat'
    };
    return <Badge className={`${styles[status] || 'bg-slate-100'} border`}>{labels[status] || status}</Badge>;
  };

  const getImportanceBadge = (importance) => {
    const styles = {
      critical: 'bg-red-100 text-red-700',
      high: 'bg-orange-100 text-orange-700',
      medium: 'bg-yellow-100 text-yellow-700',
      low: 'bg-green-100 text-green-700'
    };
    const labels = { critical: 'Critic', high: 'Ridicat', medium: 'Mediu', low: 'Scazut' };
    return <Badge className={styles[importance] || 'bg-slate-100'}>{labels[importance] || importance}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ro-RO', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(value || 0);
  };

  const filteredArticles = articles.filter(a => 
    a.name?.toLowerCase().includes(articleSearchTerm.toLowerCase()) ||
    a.code?.toLowerCase().includes(articleSearchTerm.toLowerCase())
  );

  // Filter requests
  const filteredRequests = requests.filter(req => {
    const matchStatus = statusFilter === 'all' || req.status === statusFilter;
    const matchImportance = importanceFilter === 'all' || req.importance === importanceFilter;
    
    let matchDate = true;
    if (dateFrom && req.created_at < dateFrom) matchDate = false;
    if (dateTo && req.created_at > dateTo) matchDate = false;
    
    return matchStatus && matchImportance && matchDate;
  });

  // Calculate statistics
  const stats = {
    total: filteredRequests.length,
    pending: filteredRequests.filter(r => r.status === 'pending').length,
    approved: filteredRequests.filter(r => r.status === 'approved').length,
    rejected: filteredRequests.filter(r => r.status === 'rejected').length,
    totalValue: filteredRequests.reduce((sum, r) => sum + (r.total_value || 0), 0)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">Necesar Consumabile</h1>
          <p className="text-slate-600 mt-1">Gestionare cereri de materiale si consumabile</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Reincarca
          </Button>
          <Button variant="outline" onClick={handleExportExcel}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {selectedRequests.length > 0 ? `Export ${selectedRequests.length} Selectate` : 'Export Excel'}
          </Button>
          <Button variant="outline" onClick={handleExportDetailedExcel}>
            <Download className="w-4 h-4 mr-2" />
            Export Detaliat
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Cerere Noua
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Cereri</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">In Asteptare</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Aprobate</p>
                <p className="text-2xl font-bold">{stats.approved}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Respinse</p>
                <p className="text-2xl font-bold">{stats.rejected}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Valoare Totala</p>
                <p className="text-lg font-bold">{formatCurrency(stats.totalValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate</SelectItem>
                  <SelectItem value="pending">In asteptare</SelectItem>
                  <SelectItem value="approved">Aprobate</SelectItem>
                  <SelectItem value="rejected">Respinse</SelectItem>
                  <SelectItem value="completed">Livrate</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Importanta</Label>
              <Select value={importanceFilter} onValueChange={setImportanceFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate</SelectItem>
                  <SelectItem value="critical">Critic</SelectItem>
                  <SelectItem value="high">Ridicat</SelectItem>
                  <SelectItem value="medium">Mediu</SelectItem>
                  <SelectItem value="low">Scazut</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>De la data</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label>Pana la data</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          </div>

          {(statusFilter !== 'all' || importanceFilter !== 'all' || dateFrom || dateTo) && (
            <div className="mt-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setStatusFilter('all');
                  setImportanceFilter('all');
                  setDateFrom('');
                  setDateTo('');
                }}
              >
                <X className="w-4 h-4 mr-2" />
                Reseteaza Filtre
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {hasRole('admin', 'manager') && selectedRequests.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="font-medium text-blue-900">
                {selectedRequests.length} cereri selectate
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBulkApproveDialog(true)}
                  className="bg-green-600 text-white hover:bg-green-700"
                >
                  <CheckSquare className="w-4 h-4 mr-2" />
                  Aproba Lot ({selectedRequests.length})
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedRequests([])}
                >
                  <X className="w-4 h-4 mr-2" />
                  Anuleaza Selectie
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Requests List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : filteredRequests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Nu exista cereri de consumabile</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredRequests.map((request) => (
            <Card key={request.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    {hasRole('admin', 'manager') && request.status === 'pending' && (
                      <input
                        type="checkbox"
                        className="mt-1 w-4 h-4 rounded border-slate-300"
                        checked={selectedRequests.some(r => r.id === request.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRequests([...selectedRequests, request]);
                          } else {
                            setSelectedRequests(selectedRequests.filter(r => r.id !== request.id));
                          }
                        }}
                      />
                    )}
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <ShoppingCart className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-slate-900">
                          Cerere #{request.id.substring(0, 8)}
                        </h3>
                        {getStatusBadge(request.status)}
                        {getImportanceBadge(request.importance)}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <p className="text-slate-500">Solicitant</p>
                          <p className="font-medium">{request.requester_name}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Locatie</p>
                          <p className="font-medium">{request.location_name}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Articole</p>
                          <p className="font-medium">{request.items_count} buc</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Valoare</p>
                          <p className="font-medium">{formatCurrency(request.total_value)}</p>
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-slate-400">
                        Creat: {formatDate(request.created_at)}
                        {request.approved_at && (
                          <> • Aprobat: {formatDate(request.approved_at)} de {request.approved_by_name}</>
                        )}
                      </div>
                      {request.notes && (
                        <p className="text-sm text-slate-600 mt-2 italic">"{request.notes}"</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedRequest(request);
                        setShowViewDialog(true);
                      }}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Detalii
                    </Button>
                    {hasRole('admin', 'manager') && request.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleStatusUpdate(request.id, 'approved')}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Aproba
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleStatusUpdate(request.id, 'rejected')}
                        >
                          <Ban className="w-4 h-4 mr-1" />
                          Respinge
                        </Button>
                      </>
                    )}
                    {request.requester_id === user.id && request.status === 'pending' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600"
                        onClick={() => handleDelete(request.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Cerere Noua de Consumabile</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Location and Importance */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Locatie *</Label>
                <Select
                  value={formData.location_id}
                  onValueChange={(v) => setFormData({ ...formData, location_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteaza locatie" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name} - {loc.plant_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Importanta *</Label>
                <Select
                  value={formData.importance}
                  onValueChange={(v) => setFormData({ ...formData, importance: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Scazut</SelectItem>
                    <SelectItem value="medium">Mediu</SelectItem>
                    <SelectItem value="high">Ridicat</SelectItem>
                    <SelectItem value="critical">Critic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Note / Justificare</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Motivul cererii, detalii suplimentare..."
                rows={2}
              />
            </div>

            <Separator />

            {/* Article Search */}
            <div className="space-y-2">
              <Label>Adauga Articole</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cauta articol dupa nume sau cod..."
                  value={articleSearchTerm}
                  onChange={(e) => setArticleSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {articleSearchTerm && filteredArticles.length > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                  {filteredArticles.slice(0, 10).map((article) => {
                    const availableStock = getAvailableStock(article.id);
                    const isLowStock = availableStock < (article.min_stock || 0);
                    
                    return (
                      <div
                        key={article.id}
                        onClick={() => addItemToRequest(article)}
                        className="p-3 hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900">{article.name}</p>
                              <Badge variant="outline" className="text-xs">{article.code}</Badge>
                              {isLowStock && (
                                <Badge className="bg-red-100 text-red-700 text-xs">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Stoc scazut
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                              <span>UM: <span className="font-medium">{article.unit || 'buc'}</span></span>
                              <span>Pret: <span className="font-medium">{formatCurrency(article.price || 0)}</span></span>
                              <span className={isLowStock ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                                Stoc: {availableStock} {article.unit || 'buc'}
                                {article.min_stock > 0 && ` (min: ${article.min_stock})`}
                              </span>
                            </div>
                          </div>
                          <Button type="button" size="sm" variant="ghost">
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Selected Items */}
            {formData.items.length > 0 && (
              <div className="space-y-2">
                <Label>Articole Selectate ({formData.items.length})</Label>
                <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-3">
                  {formData.items.map((item) => (
                    <div key={item.article_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{item.article_name}</p>
                        <p className="text-sm text-slate-500">{item.article_code} • {item.unit}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Input
                          type="number"
                          className="w-24 h-9"
                          value={item.quantity}
                          onChange={(e) => updateItemQuantity(item.article_id, e.target.value)}
                          min="1"
                          step="0.01"
                        />
                        <span className="text-sm text-slate-500 w-16">{item.unit}</span>
                        <span className="text-sm font-medium text-slate-700 w-24 text-right">
                          {formatCurrency(item.quantity * item.unit_price)}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItemFromRequest(item.article_id)}
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end pt-2 border-t">
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Valoare totala</p>
                      <p className="text-xl font-bold text-slate-900">
                        {formatCurrency(formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0))}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowCreateDialog(false);
                setFormData(emptyForm);
              }}>
                Anuleaza
              </Button>
              <Button type="submit" className="btn-primary" disabled={formData.items.length === 0}>
                <Send className="w-4 h-4 mr-2" />
                Trimite Cererea
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalii Cerere #{selectedRequest?.id?.substring(0, 8)}</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  {getStatusBadge(selectedRequest.status)}
                </div>
                <div>
                  <p className="text-sm text-slate-500">Importanta</p>
                  {getImportanceBadge(selectedRequest.importance)}
                </div>
                <div>
                  <p className="text-sm text-slate-500">Solicitant</p>
                  <p className="font-medium">{selectedRequest.requester_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Locatie</p>
                  <p className="font-medium">{selectedRequest.location_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Data Creare</p>
                  <p className="font-medium">{formatDate(selectedRequest.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Valoare Totala</p>
                  <p className="font-medium">{formatCurrency(selectedRequest.total_value)}</p>
                </div>
              </div>

              {selectedRequest.notes && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Note</p>
                  <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded">{selectedRequest.notes}</p>
                </div>
              )}

              <Separator />

              <div>
                <p className="text-sm font-medium text-slate-700 mb-3">Articole Solicitate:</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedRequest.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-900">{item.article_name}</p>
                        <p className="text-sm text-slate-500">{item.article_code}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{item.quantity} {item.unit}</p>
                        <p className="text-sm text-slate-500">{formatCurrency(item.quantity * item.unit_price)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedRequest.approved_at && (
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-green-900">Aprobata</p>
                  <p className="text-sm text-green-700">
                    De {selectedRequest.approved_by_name} la {formatDate(selectedRequest.approved_at)}
                  </p>
                </div>
              )}

              {selectedRequest.rejected_at && (
                <div className="bg-red-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-red-900">Respinsa</p>
                  <p className="text-sm text-red-700">
                    De {selectedRequest.rejected_by_name} la {formatDate(selectedRequest.rejected_at)}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Approve Dialog */}
      <Dialog open={showBulkApproveDialog} onOpenChange={setShowBulkApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aprobare Lot Cereri</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="font-medium text-blue-900">
                Veti aproba {selectedRequests.length} cereri simultan
              </p>
              <ul className="mt-2 space-y-1 text-sm text-blue-700">
                {selectedRequests.slice(0, 5).map(req => (
                  <li key={req.id}>• Cerere #{req.id.substring(0, 8)} - {req.requester_name}</li>
                ))}
                {selectedRequests.length > 5 && (
                  <li className="font-medium">... si inca {selectedRequests.length - 5} cereri</li>
                )}
              </ul>
            </div>
            <p className="text-sm text-slate-600">
              Aceasta actiune nu poate fi anulata. Toate cererile vor fi marcate ca aprobate.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkApproveDialog(false)}>
              Anuleaza
            </Button>
            <Button onClick={handleBulkApprove} className="bg-green-600 hover:bg-green-700">
              <CheckSquare className="w-4 h-4 mr-2" />
              Confirma Aprobare Lot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ArticleQuantityDialog
        article={pendingArticleForRequest}
        onConfirm={confirmAddItemToRequest}
        onCancel={() => setPendingArticleForRequest(null)}
      />
    </div>
  );
};

export default MaterialRequestsPageEnhanced;